import React, { useEffect, useMemo, useState } from 'react';
import { invoices as invoicesApi, emails as emailsApi, bankAccounts as bankAccountsApi, invoicesApi as invoicesExtraApi } from '@/utils/api';
import { Plus, X, CreditCard, Building2, DollarSign, Download, Mail, IndianRupee, Tag, MessageSquare, Trash2, Bell } from 'lucide-react';
import { toast } from 'sonner';
import CatalogPicker from '@/components/CatalogPicker';

const STATUS_STYLES = {
  Draft: { bg: '#E8E3D9', color: '#5C6B72' },
  Sent: { bg: '#E6F1F4', color: '#006080' },
  Viewed: { bg: '#E6F1F4', color: '#006080' },
  Partial: { bg: '#D9A44115', color: '#D9A441' },
  Paid: { bg: '#3C8A5A15', color: '#3C8A5A' },
  Overdue: { bg: '#B34A3A15', color: '#B34A3A' },
  Cancelled: { bg: '#5C6B7215', color: '#5C6B72' },
};

export default function InvoicesTab({ projectId, project, client, isOperator }) {
  const [invList, setInvList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showPayment, setShowPayment] = useState(null);
  const [lineItems, setLineItems] = useState([{ description: '', quantity: 1, unit_price: 0, amount: 0, notes: '', category: '', catalog_item_id: null }]);
  const [form, setForm] = useState({ currency: 'USD', tax_rate: 0, discount: 0, notes: '', due_date: '', payment_methods: ['bank_transfer'], bank_country: '', bank_account_id: '' });
  const [payForm, setPayForm] = useState({ amount: 0, method: 'bank_transfer', reference: '', notes: '' });
  const [bankList, setBankList] = useState([]);

  useEffect(() => {
    invoicesApi.list(projectId).then(r => setInvList(r.data)).catch(() => {}).finally(() => setLoading(false));
    if (isOperator) {
      bankAccountsApi.list(project?.brand_id).then(r => setBankList(r.data || [])).catch(() => {});
    }
  }, [projectId, isOperator, project?.brand_id]);

  // Unique countries (with currency hint) for the picker
  const countries = useMemo(() => {
    const seen = new Map();
    for (const a of bankList) {
      const key = a.country_code || a.country;
      if (!key) continue;
      if (!seen.has(key)) seen.set(key, { code: a.country_code || key, name: a.country || key, currency: a.currency });
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [bankList]);

  // Accounts for the currently-selected country
  const countryAccounts = useMemo(
    () => bankList.filter(a => (a.country_code === form.bank_country) || (a.country === form.bank_country)),
    [bankList, form.bank_country]
  );

  // When country changes, auto-select the default account for that country
  useEffect(() => {
    if (!form.bank_country) { if (form.bank_account_id) setForm(f => ({ ...f, bank_account_id: '' })); return; }
    const defaultAcct = countryAccounts.find(a => a.is_default) || countryAccounts[0];
    if (defaultAcct && defaultAcct.id !== form.bank_account_id) {
      setForm(f => ({ ...f, bank_account_id: defaultAcct.id, currency: defaultAcct.currency || f.currency }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.bank_country, countryAccounts.length]);

  const subtotal = lineItems.reduce((s, li) => s + (li.amount || li.quantity * li.unit_price || 0), 0);
  const tax = subtotal * (form.tax_rate / 100);
  const total = subtotal + tax - (form.discount || 0);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const items = lineItems.map(li => ({ ...li, amount: li.amount || li.quantity * li.unit_price }));
      const r = await invoicesApi.create(projectId, {
        ...form,
        project_id: projectId,
        line_items: items,
        client_id: client?.id || project?.client_id || '',
        brand_id: project?.brand_id || '',
        bank_account_id: form.bank_account_id || null,
      });
      setInvList(prev => [r.data, ...prev]);
      setShowCreate(false);
      toast.success('Invoice created');
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : Array.isArray(d) ? d.map(e => e.msg || JSON.stringify(e)).join(', ') : 'Failed';
      toast.error(msg);
    }
  };

  const handleStatusUpdate = async (invId, status) => {
    try {
      const r = await invoicesApi.updateStatus(projectId, invId, status);
      setInvList(prev => prev.map(i => i.id === invId ? r.data : i));
      toast.success(`Invoice marked as ${status}`);
    } catch { toast.error('Failed'); }
  };

  const handleRecordPayment = async () => {
    if (!showPayment) return;
    try {
      const r = await invoicesApi.recordPayment(projectId, showPayment, payForm);
      setInvList(prev => prev.map(i => i.id === showPayment ? r.data : i));
      setShowPayment(null);
      setPayForm({ amount: 0, method: 'bank_transfer', reference: '', notes: '' });
      toast.success('Payment recorded');
    } catch { toast.error('Failed'); }
  };

  const updateLineItem = (i, field, val) => {
    const updated = [...lineItems];
    updated[i] = { ...updated[i], [field]: ['description', 'notes', 'category'].includes(field) ? val : Number(val) };
    if (['quantity', 'unit_price'].includes(field)) updated[i].amount = updated[i].quantity * updated[i].unit_price;
    setLineItems(updated);
  };

  const pickFromCatalog = (i, item) => {
    const updated = [...lineItems];
    updated[i] = {
      ...updated[i],
      description: item.name + (item.description ? ` — ${item.description}` : ''),
      unit_price: item.default_price || updated[i].unit_price,
      quantity: item.default_hours || updated[i].quantity || 1,
      category: item.category || '',
      catalog_item_id: item.id || null,
    };
    updated[i].amount = updated[i].quantity * updated[i].unit_price;
    setLineItems(updated);
  };

  const removeLineItem = (i) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, idx) => idx !== i));
  };

  const downloadPdf = (invId, invNumber) => {
    const url = invoicesApi.pdfUrl(projectId, invId);
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.download = `${invNumber || 'invoice'}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const sendEmail = async (invId) => {
    try {
      const r = await emailsApi.sendInvoice(invId);
      if (r.data.status === 'sent') toast.success('Invoice emailed to client');
      else if (r.data.status === 'not_configured') toast.error('Configure brand SMTP in Settings > Brands first');
      else toast.error(r.data.error || 'Send failed');
      invoicesApi.list(projectId).then(rr => setInvList(rr.data));
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
  };

  const payNow = async (inv, provider) => {
    try {
      if (provider === 'stripe') {
        const r = await invoicesApi.payStripe(projectId, inv.id);
        window.location.href = r.data.checkout_url;
      } else if (provider === 'paypal') {
        const r = await invoicesApi.payPaypal(projectId, inv.id);
        window.location.href = r.data.approve_url;
      } else if (provider === 'razorpay') {
        const r = await invoicesApi.payRazorpay(projectId, inv.id);
        const loadRz = () => new Promise((resolve) => {
          if (window.Razorpay) return resolve();
          const s = document.createElement('script');
          s.src = 'https://checkout.razorpay.com/v1/checkout.js';
          s.onload = resolve;
          document.body.appendChild(s);
        });
        await loadRz();
        const opts = {
          key: r.data.key_id,
          amount: r.data.amount,
          currency: r.data.currency,
          order_id: r.data.order_id,
          name: 'Invoice Payment',
          description: inv.invoice_number,
          handler: async (resp) => {
            try {
              await invoicesApi.verifyRazorpay({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
                invoice_id: inv.id,
                amount: inv.total,
              });
              toast.success('Payment successful');
              invoicesApi.list(projectId).then(rr => setInvList(rr.data));
            } catch { toast.error('Verification failed'); }
          },
        };
        new window.Razorpay(opts).open();
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : `${provider} not configured`);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Invoices</h3>
        {isOperator && (
          <button className="btn-primary text-xs flex items-center gap-1.5 px-3 py-1.5" onClick={() => setShowCreate(true)} data-testid="create-invoice-button">
            <Plus size={13} /> New Invoice
          </button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">{[1].map(i => <div key={i} className="h-24 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />)}</div>
      ) : invList.length === 0 ? (
        <div className="card-base p-8 text-center">
          <DollarSign size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No invoices yet</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="invoices-list">
          {invList.map(inv => {
            const st = STATUS_STYLES[inv.status] || STATUS_STYLES.Draft;
            return (
              <div key={inv.id} className="card-base p-4" data-testid={`invoice-${inv.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{inv.invoice_number}</span>
                      <span className="badge-status" style={{ background: st.bg, color: st.color }}>{inv.status}</span>
                    </div>
                    {inv.due_date && <p className="text-xs" style={{ color: 'var(--muted)' }}>Due: {new Date(inv.due_date).toLocaleDateString()}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-black" style={{ color: 'var(--ink)' }}>{inv.currency} {inv.total?.toLocaleString()}</p>
                    {inv.paid_amount > 0 && (
                      <p className="text-xs" style={{ color: '#3C8A5A' }}>Paid: {inv.currency} {inv.paid_amount?.toLocaleString()}</p>
                    )}
                  </div>
                </div>

                {/* Line items preview */}
                {inv.line_items?.length > 0 && (
                  <div className="space-y-1 mb-3">
                    {inv.line_items.map((li, i) => (
                      <div key={i} className="py-1 border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
                        <div className="flex items-center justify-between text-sm">
                          <span style={{ color: 'var(--ink)' }} className="flex items-center gap-2">
                            {li.description}
                            {li.category && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ background: '#E6F1F4', color: '#006080' }}>{li.category}</span>
                            )}
                          </span>
                          <span style={{ color: 'var(--muted)' }}>{inv.currency} {(li.amount || li.quantity * li.unit_price)?.toLocaleString()}</span>
                        </div>
                        {li.notes && (
                          <p className="text-xs mt-0.5 flex items-start gap-1" style={{ color: 'var(--muted)' }}>
                            <MessageSquare size={10} className="mt-0.5 flex-shrink-0" /> <span>{li.notes}</span>
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Payment methods */}
                {!isOperator && inv.status !== 'Paid' && inv.status !== 'Draft' && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Pay via</p>
                    <div className="flex gap-2 flex-wrap">
                      {(inv.payment_methods || []).includes('stripe') && (
                        <button className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-btn font-semibold transition-colors hover:opacity-80" style={{ background: '#635BFF', color: 'white' }} onClick={() => payNow(inv, 'stripe')} data-testid={`pay-stripe-${inv.id}`}>
                          <CreditCard size={12} /> Pay with Card
                        </button>
                      )}
                      {(inv.payment_methods || []).includes('paypal') && (
                        <button className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-btn font-semibold transition-colors hover:opacity-80" style={{ background: '#003087', color: 'white' }} onClick={() => payNow(inv, 'paypal')} data-testid={`pay-paypal-${inv.id}`}>
                          PayPal
                        </button>
                      )}
                      {(inv.payment_methods || []).includes('razorpay') && (
                        <button className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-btn font-semibold transition-colors hover:opacity-80" style={{ background: '#3395FF', color: 'white' }} onClick={() => payNow(inv, 'razorpay')} data-testid={`pay-razorpay-${inv.id}`}>
                          <IndianRupee size={12} /> Razorpay
                        </button>
                      )}
                      {(inv.payment_methods || []).includes('bank_transfer') && (
                        <div className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-btn" style={{ background: '#E6F1F4', color: '#006080' }}>
                          <Building2 size={12} /> Bank Transfer
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Bank details for wire transfer (from invoice snapshot or brand fallback) */}
                {inv.status !== 'Draft' && (inv.bank_details_text || inv.bank_account_snapshot) && (
                  <div className="mb-3 p-3 rounded-btn" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }} data-testid={`invoice-bank-block-${inv.id}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: 'var(--muted)' }}>
                        <Building2 size={10} /> Bank transfer details
                        {inv.bank_account_snapshot?.nickname && (
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-sm" style={{ background: '#E6F1F4', color: '#006080' }}>
                            {inv.bank_account_snapshot.nickname}{inv.bank_account_snapshot.country ? ` · ${inv.bank_account_snapshot.country}` : ''}
                          </span>
                        )}
                      </p>
                      <button
                        className="text-xs font-semibold"
                        style={{ color: '#006080' }}
                        onClick={() => { navigator.clipboard.writeText(inv.bank_details_text || ''); toast.success('Bank details copied'); }}
                        data-testid={`copy-bank-${inv.id}`}
                      >
                        Copy all
                      </button>
                    </div>
                    <div className="space-y-0.5">
                      {(inv.bank_details_text || '').split('\n').map((ln, i) => {
                        const idx = ln.indexOf(':');
                        const label = idx > 0 ? ln.slice(0, idx) : '';
                        const value = idx > 0 ? ln.slice(idx + 1).trim() : ln;
                        return (
                          <div key={i} className="flex items-center gap-2 text-xs">
                            {label && <span className="font-semibold w-32 flex-shrink-0" style={{ color: 'var(--muted)' }}>{label}</span>}
                            <span className="font-mono flex-1 truncate" style={{ color: 'var(--ink)' }}>{value}</span>
                            {value && (
                              <button
                                className="text-[10px] px-1.5 py-0.5 rounded-sm flex-shrink-0"
                                style={{ color: '#006080', background: '#E6F1F4' }}
                                onClick={() => { navigator.clipboard.writeText(value); toast.success('Copied'); }}
                              >
                                copy
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Actions row (all users) */}
                <div className="flex gap-2 flex-wrap items-center">
                  <button className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1 transition-colors" style={{ background: 'var(--surface)', color: 'var(--muted)', border: '1px solid var(--border)' }} onClick={() => downloadPdf(inv.id, inv.invoice_number)} data-testid={`download-invoice-${inv.id}`}>
                    <Download size={12} /> PDF
                  </button>
                  {isOperator && inv.status !== 'Draft' && (
                    <button className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#E6F1F4', color: '#006080' }} onClick={() => sendEmail(inv.id)} data-testid={`email-invoice-${inv.id}`}>
                      <Mail size={12} /> Email Client
                    </button>
                  )}
                  {isOperator && ['Sent','Overdue','Partial'].includes(inv.status) && (
                    <button
                      className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1"
                      style={{ background: '#D9A44115', color: '#8A6B1A' }}
                      onClick={async () => {
                        try {
                          const r = await invoicesExtraApi.sendReminder(projectId, inv.id);
                          if (r.data.status === 'sent') toast.success('Reminder sent');
                          else toast.error(r.data.error || 'Reminder failed');
                          // Refresh list to show updated reminder_count
                          const refreshed = await invoicesApi.list(projectId);
                          setInvList(refreshed.data);
                        } catch (e) { toast.error(e.response?.data?.detail || 'Reminder failed'); }
                      }}
                      data-testid={`remind-invoice-${inv.id}`}
                      title={inv.reminder_count ? `Sent ${inv.reminder_count} reminder${inv.reminder_count > 1 ? 's' : ''} so far` : 'Send a payment reminder now'}
                    >
                      <Bell size={12} /> Send reminder{inv.reminder_count ? ` (${inv.reminder_count})` : ''}
                    </button>
                  )}
                {isOperator && inv.status !== 'Paid' && inv.status !== 'Cancelled' && (
                  <>
                    {inv.status === 'Draft' && (
                      <button className="text-xs px-3 py-1.5 rounded-btn font-semibold" style={{ background: '#E6F1F4', color: '#006080' }} onClick={() => handleStatusUpdate(inv.id, 'Sent')} data-testid={`send-invoice-${inv.id}`}>
                        Mark as Sent
                      </button>
                    )}
                    <button className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#3C8A5A15', color: '#3C8A5A' }} onClick={() => setShowPayment(inv.id)} data-testid={`record-payment-${inv.id}`}>
                      <CreditCard size={12} /> Record Payment
                    </button>
                  </>
                )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Record payment modal */}
      {showPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-sm p-5 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--ink)' }}>Record Payment</h3>
              <button onClick={() => setShowPayment(null)} style={{ color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Amount</label><input className="input-agency text-sm" type="number" min="0" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: Number(e.target.value)})} /></div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Method</label>
                <select className="input-agency text-sm" value={payForm.method} onChange={e => setPayForm({...payForm, method: e.target.value})}>
                  {['bank_transfer','stripe','paypal','razorpay','manual'].map(m => <option key={m} value={m}>{m.replace('_',' ')}</option>)}
                </select>
              </div>
              <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Reference / Transaction ID</label><input className="input-agency text-sm font-mono" value={payForm.reference} onChange={e => setPayForm({...payForm, reference: e.target.value})} /></div>
              <div className="flex gap-3"><button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setShowPayment(null)}>Cancel</button><button type="button" className="flex-1 btn-primary py-2 text-sm" onClick={handleRecordPayment} data-testid="record-payment-submit">Record</button></div>
            </div>
          </div>
        </div>
      )}

      {/* Create invoice modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="card-base w-full max-w-2xl p-5 animate-fade-in overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold" style={{ color: 'var(--ink)' }}>New Invoice</h3>
              <button onClick={() => setShowCreate(false)} style={{ color: 'var(--muted)' }}><X size={16} /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Line Items</label>
                  <button type="button" className="text-xs" style={{ color: '#006080' }} onClick={() => setLineItems(prev => [...prev, { description: '', quantity: 1, unit_price: 0, amount: 0, notes: '', category: '', catalog_item_id: null }])} data-testid="add-line-item-button">+ Add Line</button>
                </div>
                <div className="space-y-3">
                  {lineItems.map((li, i) => (
                    <div key={i} className="p-2 rounded-btn" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }} data-testid={`inv-line-${i}`}>
                      <div className="grid grid-cols-12 gap-1.5 items-center">
                        <input className="input-agency text-sm col-span-6" placeholder="Service..." value={li.description} onChange={e => updateLineItem(i, 'description', e.target.value)} required data-testid={`inv-line-desc-${i}`} />
                        <input className="input-agency text-sm col-span-2 text-center" type="number" step="0.25" min="0" placeholder="Qty" value={li.quantity} onChange={e => updateLineItem(i, 'quantity', e.target.value)} />
                        <input className="input-agency text-sm col-span-2 text-center" type="number" min="0" placeholder="Price" value={li.unit_price} onChange={e => updateLineItem(i, 'unit_price', e.target.value)} />
                        <div className="col-span-1 text-xs tabular-nums text-right font-semibold" style={{ color: 'var(--ink)' }}>{(li.amount || li.quantity * li.unit_price).toFixed(2)}</div>
                        <button type="button" onClick={() => removeLineItem(i)} className="col-span-1 p-1" style={{ color: lineItems.length > 1 ? '#B34A3A' : 'var(--border)' }} disabled={lineItems.length <= 1}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <CatalogPicker context="invoice" onPick={(it) => pickFromCatalog(i, it)} />
                        {li.category && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm flex items-center gap-0.5" style={{ background: '#E6F1F4', color: '#006080' }}>
                            <Tag size={8} /> {li.category}
                          </span>
                        )}
                        <input className="flex-1 text-xs px-2 py-1.5 rounded-btn border outline-none" style={{ borderColor: 'var(--border)', background: 'var(--surface)', color: 'var(--ink)' }} placeholder="Remark / comment for this line (optional)" value={li.notes || ''} onChange={e => updateLineItem(i, 'notes', e.target.value)} data-testid={`inv-line-notes-${i}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Tax %</label><input className="input-agency text-sm" type="number" min="0" max="100" value={form.tax_rate} onChange={e => setForm({...form, tax_rate: Number(e.target.value)})} /></div>
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Discount</label><input className="input-agency text-sm" type="number" min="0" value={form.discount} onChange={e => setForm({...form, discount: Number(e.target.value)})} /></div>
                <div><label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Due Date</label><input className="input-agency text-sm" type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Payment methods accepted</label>
                <div className="flex gap-2 flex-wrap">
                  {['stripe', 'paypal', 'razorpay', 'bank_transfer'].map(m => (
                    <label key={m} className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-btn cursor-pointer" style={{ background: form.payment_methods.includes(m) ? '#E6F1F4' : 'var(--bg)', color: form.payment_methods.includes(m) ? '#006080' : 'var(--muted)', border: '1px solid var(--border)' }}>
                      <input type="checkbox" className="sr-only" checked={form.payment_methods.includes(m)} onChange={e => {
                        setForm(f => ({ ...f, payment_methods: e.target.checked ? [...f.payment_methods, m] : f.payment_methods.filter(x => x !== m) }));
                      }} />
                      {m.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </label>
                  ))}
                </div>
              </div>

              {form.payment_methods.includes('bank_transfer') && bankList.length > 0 && (
                <div className="p-3 rounded-btn" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <p className="text-xs font-bold uppercase tracking-wide mb-2 flex items-center gap-1.5" style={{ color: 'var(--muted)' }}>
                    <Building2 size={11} /> Bank account (for wire transfer)
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Country</label>
                      <select className="input-agency text-sm" value={form.bank_country} onChange={e => setForm({ ...form, bank_country: e.target.value })} data-testid="invoice-bank-country">
                        <option value="">— none (use brand default) —</option>
                        {countries.map(c => (
                          <option key={c.code} value={c.code}>{c.name} · {c.currency || ''}</option>
                        ))}
                      </select>
                    </div>
                    {countryAccounts.length > 1 && (
                      <div>
                        <label className="block text-[10px] font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>Account</label>
                        <select className="input-agency text-sm" value={form.bank_account_id} onChange={e => setForm({ ...form, bank_account_id: e.target.value })} data-testid="invoice-bank-account">
                          {countryAccounts.map(a => (
                            <option key={a.id} value={a.id}>{a.nickname}{a.is_default ? ' (default)' : ''}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  {form.bank_account_id && (() => {
                    const sel = bankList.find(a => a.id === form.bank_account_id);
                    if (!sel) return null;
                    return (
                      <div className="mt-2 text-xs p-2 rounded-btn font-mono whitespace-pre-wrap" style={{ background: 'var(--surface)', color: 'var(--ink)' }} data-testid="invoice-bank-preview">
                        {[`Bank: ${sel.bank_name}`, `Account holder: ${sel.account_holder}`, ...(sel.rows || []).map(r => `${r.label}: ${r.value}`), sel.currency && `Currency: ${sel.currency}`].filter(Boolean).join('\n')}
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="p-3 rounded-btn flex justify-between" style={{ background: 'var(--bg)' }}>
                <span className="text-sm" style={{ color: 'var(--muted)' }}>Total</span>
                <span className="text-lg font-black" style={{ color: 'var(--ink)' }}>{form.currency} {total.toFixed(2)}</span>
              </div>
              <div className="flex gap-3"><button type="button" className="flex-1 py-2 text-sm rounded-btn border font-medium" style={{ borderColor: 'var(--border)', color: 'var(--muted)' }} onClick={() => setShowCreate(false)}>Cancel</button><button type="submit" className="flex-1 btn-primary py-2 text-sm" data-testid="create-invoice-submit">Create Invoice</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
